import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  X,
  Search,
  Loader2,
  Grid,
  Boxes,
  Package,
  Box,
  AlertCircle,
  ShoppingBag,
  CheckCircle2,
  XCircle,
  Filter,
  ArrowLeft,
  BadgeDollarSign,
  Eye,
  Sparkles,
  TrendingUp,
  Wallet,
  Hash,
  DollarSign,
  Calculator,
  Info,
} from "lucide-react";
import { fetchProducts } from "../../../RTK/productSlice";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const TABS = [
  { key: "all", label: "الكل", icon: Grid, idx: 0 },
  { key: "quantity", label: "كمية", icon: Boxes, idx: 1 },
  { key: "package", label: "باقة", icon: Package, idx: 2 },
  { key: "stock", label: "مخزون", icon: Box, idx: 3 },
];

const FILTER_OPTIONS = [
  { value: "All", label: "الكل", icon: Filter, color: "text-slate-400" },
  { value: "Available", label: "متوفر", icon: CheckCircle2, color: "text-emerald-400" },
  { value: "Unavailable", label: "غير متوفر", icon: XCircle, color: "text-red-400" },
];

/* ─────────────────────────────────────────────
   PRODUCT CARD
───────────────────────────────────────────── */
const ProductCard = ({ product, selected, onClick }) => {
  const available = product.status?.toLowerCase() === "available";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative group rounded-xl border p-2 flex flex-col items-center gap-1.5
        transition-all duration-200 hover:scale-[1.03] active:scale-95
        ${selected
          ? "border-yellow-400 bg-yellow-400/10 shadow-lg shadow-yellow-400/20 ring-1 ring-yellow-400/30"
          : available
            ? "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/60 hover:bg-emerald-500/10"
            : "border-red-500/30 bg-red-500/5 hover:border-red-400/60 hover:bg-red-500/10"
        }
      `}
    >
      {selected && (
        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center shadow">
          <CheckCircle2 className="w-2.5 h-2.5 text-black" />
        </span>
      )}
      <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-slate-800">
        {product.image ? (
          <img src={product.image} alt={product.alt_text || product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="w-7 h-7 text-slate-600" />
          </div>
        )}
        <span className={`absolute top-1 left-1 w-2 h-2 rounded-full border border-slate-900 ${available ? "bg-emerald-400" : "bg-red-400"}`} />
      </div>
      <p className="text-white text-[11px] font-medium leading-tight truncate w-full text-center px-0.5">
        {product.name}
      </p>
    </button>
  );
};

/* ─────────────────────────────────────────────
   PREVIEW CARD
───────────────────────────────────────────── */
const PreviewCard = ({ preview }) => (
  <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
    <div className="rounded-2xl bg-slate-900/80 border border-slate-700/60 p-4 space-y-3">
      <div className="flex items-center gap-3">
        {preview.product?.image && (
          <img src={preview.product.image} alt={preview.product.name} className="w-10 h-10 rounded-lg object-cover border border-slate-700" />
        )}
        <div>
          <p className="text-white font-semibold text-sm">{preview.product?.name}</p>
          <p className="text-xs text-slate-400">{preview.product?.product_type}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "الكمية", value: preview.quantity, icon: Hash },
          { label: "نسبة الربح", value: `${preview.profit_percentage}%`, icon: TrendingUp },
          { label: "سعر التكلفة", value: `$${Number(preview.cost_total).toFixed(3)}`, icon: BadgeDollarSign },
          { label: "صافي الربح", value: `$${Number(preview.profit_total).toFixed(3)}`, icon: BadgeDollarSign },
          { label: "الإجمالي", value: `$${Number(preview.final_price_total).toFixed(2)}`, icon: Wallet },
        ].map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className={`bg-slate-800/60 rounded-xl p-2.5 flex items-start gap-2 ${label.trim() === "الإجمالي" ? "col-span-2" : ""}`}
          >
            <Icon className="w-3.5 h-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[10px] text-slate-400 leading-none mb-1">{label}</p>
              <p className="text-white text-xs font-semibold">{value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="rounded-2xl bg-slate-900/80 border border-slate-700/60 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">أرخص وكيل</span>
        <span className="text-yellow-300 font-semibold text-sm">{preview.cheapest_agent_name}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">رصيد الموظف</span>
        <span className="text-white text-sm font-mono">${Number(preview.staff_balance).toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">كافٍ للشراء</span>
        <span className={`text-sm font-bold flex items-center gap-1 ${preview.sufficient_balance ? "text-emerald-400" : "text-red-400"}`}>
          {preview.sufficient_balance ? <><CheckCircle2 className="w-3.5 h-3.5" /> نعم</> : <><XCircle className="w-3.5 h-3.5" /> لا</>}
        </span>
      </div>
    </div>
    {!preview.sufficient_balance && (
      <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-center gap-2 text-sm text-red-300">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        الرصيد غير كافٍ لإتمام الطلب
      </div>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   CHARGE MODE TABS
───────────────────────────────────────────── */
const ChargeModeTab = ({ mode, setMode }) => (
  <div className="flex gap-1 p-1 bg-slate-800/80 rounded-xl border border-slate-700/60">
    <button
      type="button"
      onClick={() => setMode("quantity")}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
        mode === "quantity"
          ? "bg-yellow-400 text-black shadow-md"
          : "text-slate-400 hover:text-white hover:bg-slate-700/60"
      }`}
    >
      <Hash className="w-3.5 h-3.5" />
      شحن بالكمية
    </button>
    <button
      type="button"
      onClick={() => setMode("price")}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
        mode === "price"
          ? "bg-yellow-400 text-black shadow-md"
          : "text-slate-400 hover:text-white hover:bg-slate-700/60"
      }`}
    >
      <DollarSign className="w-3.5 h-3.5" />
      شحن بالسعر
    </button>
  </div>
);

/* ─────────────────────────────────────────────
   QUANTITY RANGE BADGE
───────────────────────────────────────────── */
const QtyRangeBadge = ({ min, max }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-xl">
    <Info className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
    <span className="text-[11px] text-slate-400">
      النطاق المسموح:{" "}
      <span className="text-yellow-300 font-semibold">{min?.toLocaleString()}</span>
      {" "}–{" "}
      <span className="text-yellow-300 font-semibold">{max?.toLocaleString()}</span>
    </span>
  </div>
);

/* ─────────────────────────────────────────────
   MAIN POPUP
───────────────────────────────────────────── */
const CreateOrderPopup = ({ onClose, onCreated }) => {
  const dispatch = useDispatch();
  const productsState = useSelector((state) => state.products);

  /* ── product list state ── */
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOption, setFilterOption] = useState("All");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  /* ── product details (from /Products/{id}/) ── */
  const [productDetails, setProductDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  /* ── order state ── */
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [chargeMode, setChargeMode] = useState("quantity"); // "quantity" | "price"
  const [quantity, setQuantity] = useState(1);
  const [priceInput, setPriceInput] = useState(""); // for price mode (in $)
  const [profitPercentage, setProfitPercentage] = useState();
  const [accountId, setAccountId] = useState("");

  /* ── preview state ── */
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  /* ── submit state ── */
  const [submitting, setSubmitting] = useState(false);

  /* ── mobile panel ── */
  const [mobilePanel, setMobilePanel] = useState("products");

  /* ── derived qty values from productDetails ── */
  const qtyMin = productDetails?.qty_values?.min ?? 1;
  const qtyMax = productDetails?.qty_values?.max ?? Infinity;
  const productPrice = parseFloat(productDetails?.price ?? "0");
  const isQuantityType = productDetails?.product_type === "quantity" ?? selectedProduct?.product_type === "quantity";

  /* ── qty calculated from price input ── */
  // effectiveUnitPrice = productPrice × (1 + profit% / 100)
  // e.g. price=0.01, profit=2% → effectiveUnitPrice=0.0102 → qty = input / 0.0102
  const effectiveUnitPrice = useMemo(() => {
    if (!productPrice) return 0;
    return productPrice * (1 + profitPercentage / 100);
  }, [productPrice, profitPercentage]);

  const calculatedQty = useMemo(() => {
    if (chargeMode !== "price" || !priceInput || !effectiveUnitPrice) return 0;
    return Math.floor(parseFloat(priceInput) / effectiveUnitPrice);
  }, [chargeMode, priceInput, effectiveUnitPrice]);

  /* ── effective quantity sent to API ── */
  const effectiveQty = chargeMode === "price" ? calculatedQty : quantity;

  /* ── quantity validation ── */
  const qtyError = useMemo(() => {
    if (!productDetails) return null;
    const q = effectiveQty;
    if (!q || q < 1) return "الكمية يجب أن تكون على الأقل 1";
    if (q < qtyMin) return `الحد الأدنى للكمية هو ${qtyMin.toLocaleString()}`;
    if (qtyMax !== Infinity && q > qtyMax) return `الحد الأقصى للكمية هو ${qtyMax.toLocaleString()}`;
    return null;
  }, [effectiveQty, qtyMin, qtyMax, productDetails]);

  /* ── fetch products on mount ── */
  useEffect(() => {
    if (
      !productsState.products.all.length &&
      !productsState.products.quantity.length &&
      !productsState.products.package.length &&
      !productsState.products.stock.length
    ) {
      dispatch(fetchProducts("all"));
    }
  }, [dispatch, productsState.products]);

  /* ── fetch product details when a product is selected ── */
  const fetchProductDetails = useCallback(async (productId) => {
    setLoadingDetails(true);
    setProductDetails(null);
    try {
      const res = await axios.get(`${apiUrl}/Products/${productId}/`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setProductDetails(res.data);
      // reset quantity to min value from API
      const min = res.data?.qty_values?.min ?? 1;
      setQuantity(min);
    } catch (err) {
      toast.error("تعذّر تحميل تفاصيل المنتج");
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  /* ── close filter on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── derive product list ── */
  const products = useMemo(() => {
    const key = TABS[activeTabIdx].key;
    return productsState.products[key] || [];
  }, [productsState.products, activeTabIdx]);

  const filteredProducts = useMemo(() => {
    let list = Array.isArray(products) ? products : [];
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(term) ||
          p.id?.toString().includes(term) ||
          p.product_type?.toLowerCase().includes(term),
      );
    }
    if (filterOption !== "All") {
      list = list.filter((p) => p.status?.toLowerCase() === filterOption.toLowerCase());
    }
    return list;
  }, [products, searchQuery, filterOption]);

  /* ── helpers ── */
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setPreview(null);
    setPreviewError(null);
    setPriceInput("");
    setChargeMode("quantity");
    fetchProductDetails(product.id);
    setMobilePanel("order");
  };

  const handleTabChange = (idx) => {
    setActiveTabIdx(idx);
    setSelectedProduct(null);
    setProductDetails(null);
    setPreview(null);
    setPreviewError(null);
  };

  const handleChargeModeChange = (mode) => {
    setChargeMode(mode);
    setPreview(null);
    setPreviewError(null);
    setPriceInput("");
  };

  const handleQuantityChange = (val) => {
    const n = Math.max(1, Number(val));
    setQuantity(n);
    setPreview(null);
    setPreviewError(null);
  };

  const handlePreview = async () => {
    if (!selectedProduct) { toast.error("اختر منتجاً أولاً"); return; }
    if (qtyError) { toast.error(qtyError); return; }
    if (!effectiveQty || effectiveQty < 1) { toast.error("الكمية غير صالحة"); return; }

    setLoadingPreview(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const res = await axios.get(`${apiUrl}/StaffOrder/preview/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: {
          product_id: selectedProduct.id,
          quantity: effectiveQty,
          profit_percentage: profitPercentage,
        },
      });
      if (res.data?.error) setPreviewError(res.data.error);
      else setPreview(res.data);
    } catch (err) {
      setPreviewError(
        err.response?.data?.error || err.response?.data?.message || "حدث خطأ أثناء جلب المعاينة",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct) { toast.error("اختر منتجاً أولاً"); return; }
    if (!accountId.trim()) { toast.error("ادخل account_id"); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${apiUrl}/StaffOrder/`,
        {
          product_id: selectedProduct.id,
          quantity: effectiveQty,
          account_id: accountId.trim(),
          profit_percentage: profitPercentage,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );
      toast.success("تم إنشاء الطلب بنجاح");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || "فشل إنشاء الطلب");
    } finally {
      setSubmitting(false);
    }
  };

  const canCreate = !!preview && preview.sufficient_balance && !!accountId.trim() && !submitting && !qtyError;

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 bg-black/70 z-[2500] flex items-center justify-center p-3 sm:p-4" dir="rtl">
      <div className="bg-slate-900 text-white rounded-2xl w-full max-w-[1200px] max-h-[92vh] flex flex-col shadow-2xl border border-slate-700/50 overflow-hidden">

        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-yellow-400/10 border border-yellow-400/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-none">إنشاء طلب جديد</h2>
              <p className="text-xs text-slate-400 mt-0.5">اختر منتجاً ثم أدخل التفاصيل</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 hover:bg-slate-800 transition" title="إغلاق">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* ── MOBILE PANEL TOGGLE ── */}
        <div className="flex lg:hidden border-b border-slate-800 flex-shrink-0">
          {[
            { key: "products", label: "المنتجات" },
            { key: "order", label: selectedProduct ? `تفاصيل: ${selectedProduct.name}` : "تفاصيل الطلب" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setMobilePanel(key)}
              className={`flex-1 py-2.5 text-sm font-medium transition ${mobilePanel === key ? "text-yellow-400 border-b-2 border-yellow-400" : "text-slate-400"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── BODY ── */}
        <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row">

          {/* ════ LEFT: Products Panel ════ */}
          <div className={`flex flex-col flex-1 overflow-hidden ${mobilePanel !== "products" ? "hidden lg:flex" : "flex"}`}>
            {/* toolbar */}
            <div className="flex-shrink-0 px-4 pt-4 pb-3 space-y-3 border-b border-slate-800/60">
              <div className="flex gap-1.5 p-1 bg-slate-800/60 rounded-xl">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => handleTabChange(tab.idx)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition ${
                        activeTabIdx === tab.idx
                          ? "bg-yellow-400 text-black shadow"
                          : "text-slate-400 hover:text-white hover:bg-slate-700/60"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث بالاسم أو الكود..."
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl py-2 pr-9 pl-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-yellow-400/60 transition"
                  />
                </div>
                <div className="relative" ref={filterRef}>
                  <button
                    onClick={() => setFilterOpen((v) => !v)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-300 hover:text-white hover:border-slate-600 transition"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">
                      {filterOption === "All" ? "الكل" : filterOption === "Available" ? "متوفر" : "غير متوفر"}
                    </span>
                  </button>
                  {filterOpen && (
                    <div className="absolute left-0 mt-1 w-36 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                      {FILTER_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => { setFilterOption(opt.value); setFilterOpen(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition"
                          >
                            <Icon className={`w-3.5 h-3.5 ${opt.color}`} />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-[10px] text-slate-500">
                {filteredProducts.length} منتج
                {selectedProduct && (
                  <span className="mr-2 text-yellow-400">• تم اختيار: {selectedProduct.name}</span>
                )}
              </p>
            </div>

            {/* products grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {productsState.loading ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
                  <span className="text-sm">جاري تحميل المنتجات...</span>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-500">
                  <ShoppingBag className="w-10 h-10 opacity-30" />
                  <p className="text-sm">لا يوجد منتجات</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      selected={selectedProduct?.id === product.id}
                      onClick={() => handleSelectProduct(product)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ════ DIVIDER (desktop) ════ */}
          <div className="hidden lg:block w-px bg-slate-800 flex-shrink-0" />

          {/* ════ RIGHT: Order Details Panel ════ */}
          <div className={`flex flex-col w-full lg:w-[360px] xl:w-[400px] flex-shrink-0 overflow-hidden ${mobilePanel !== "order" ? "hidden lg:flex" : "flex"}`}>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">

              {/* mobile back */}
              <button
                onClick={() => setMobilePanel("products")}
                className="lg:hidden flex items-center gap-2 text-xs text-slate-400 hover:text-white transition"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                العودة للمنتجات
              </button>

              {/* no product selected */}
              {!selectedProduct ? (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 py-16">
                  <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                    <ShoppingBag className="w-7 h-7 opacity-50" />
                  </div>
                  <p className="text-sm text-center">اختر منتجاً من القائمة لبدء الطلب</p>
                </div>
              ) : (
                <>
                  {/* selected product header */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-2xl p-3 border border-slate-700/60">
                    {selectedProduct.image ? (
                      <img src={selectedProduct.image} alt={selectedProduct.name} className="w-12 h-12 rounded-xl object-cover border border-slate-700" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {loadingDetails ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-yellow-400" />
                          <span className="text-xs text-slate-400">جاري تحميل التفاصيل...</span>
                        </div>
                      ) : (
                        <>
                          <p className="text-white font-semibold text-sm truncate">{productDetails?.name ?? selectedProduct.name}</p>
                          <p className="text-xs text-slate-400">
                            #{selectedProduct.id} · {productDetails?.product_type ?? selectedProduct.product_type}
                            {productDetails?.price && (
                              <span className="mr-2 text-yellow-300 font-mono">${parseFloat(productDetails.price).toFixed(4)}</span>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      selectedProduct.status?.toLowerCase() === "available"
                        ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                        : "bg-red-500/15 text-red-400 border border-red-500/30"
                    }`}>
                      {selectedProduct.status?.toLowerCase() === "available" ? "متوفر" : "غير متوفر"}
                    </span>
                  </div>

                  {/* ── charge mode tabs ── */}
                  {!loadingDetails && (
                    <>
                      <ChargeModeTab mode={chargeMode} setMode={handleChargeModeChange} />

                      <div className="space-y-3">

                        {/* qty range badge */}
                        {productDetails?.qty_values && isQuantityType && (
                          <QtyRangeBadge min={qtyMin} max={productDetails.qty_values.max} />
                        )}

                        {/* ── QUANTITY MODE ── */}
                        {chargeMode === "quantity" && (
                          <div className="space-y-1.5">
                            <label className="text-[11px] text-slate-400 font-medium">الكمية</label>
                            <input
                              type="number"
                              min={qtyMin}
                              max={qtyMax !== Infinity ? qtyMax : undefined}
                              value={quantity}
                              onChange={(e) => handleQuantityChange(e.target.value)}
                              disabled={!isQuantityType}
                              className={`w-full rounded-xl border px-3 py-2.5 bg-slate-800 text-white text-sm focus:outline-none transition ${
                                qtyError
                                  ? "border-red-500/60 focus:border-red-400"
                                  : !isQuantityType
                                    ? "opacity-50 cursor-not-allowed border-slate-700"
                                    : "border-slate-700 focus:border-yellow-400/60"
                              }`}
                            />
                            {!isQuantityType && (
                              <p className="text-[10px] text-slate-500">الكمية ثابتة (1) لهذا النوع</p>
                            )}
                            {qtyError && isQuantityType && (
                              <p className="text-[11px] text-red-400 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> {qtyError}
                              </p>
                            )}

                            {/* calculated price display */}
                            {quantity > 0 && effectiveUnitPrice > 0 && (
                              <div className={`rounded-xl border p-3 flex items-start gap-3 transition-all ${
                                !qtyError ? "border-yellow-400/30 bg-yellow-400/5" : "border-slate-700/60 bg-slate-800/40"
                              }`}>
                                <Wallet className={`w-4 h-4 flex-shrink-0 mt-0.5 ${!qtyError ? "text-yellow-400" : "text-slate-500"}`} />
                                <div className="space-y-1 min-w-0">
                                  <p className="text-[10px] text-slate-400">السعر المحسوب</p>
                                  <p className={`text-sm font-bold ${!qtyError ? "text-white" : "text-slate-600"}`}>
                                    ${(quantity * effectiveUnitPrice).toFixed(2)}
                                  </p>
                                  
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── PRICE MODE ── */}
                        {chargeMode === "price" && (
                          <div className="space-y-3">
                            <div className="space-y-1.5">
                              <label className="text-[11px] text-slate-400 font-medium">المبلغ بالدولار ($)</label>
                              <div className="relative">
                                <DollarSign className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={priceInput}
                                  onChange={(e) => {
                                    setPriceInput(e.target.value);
                                    setPreview(null);
                                    setPreviewError(null);
                                  }}
                                  placeholder="مثال: 10.00"
                                  className="w-full rounded-xl border border-slate-700 px-3 py-2.5 pr-9 bg-slate-800 text-white text-sm focus:outline-none focus:border-yellow-400/60 transition placeholder-slate-600"
                                />
                              </div>
                            </div>

                            {/* calculated quantity display */}
                            <div className={`rounded-xl border p-3 flex items-start gap-3 transition-all ${
                              calculatedQty > 0 && !qtyError
                                ? "border-yellow-400/30 bg-yellow-400/5"
                                : "border-slate-700/60 bg-slate-800/40"
                            }`}>
                              <Calculator className={`w-4 h-4 flex-shrink-0 mt-0.5 ${calculatedQty > 0 ? "text-yellow-400" : "text-slate-500"}`} />
                              <div className="space-y-1 min-w-0">
                                <p className="text-[10px] text-slate-400">الكمية المحسوبة</p>
                                <p className={`text-sm font-bold ${calculatedQty > 0 ? "text-white" : "text-slate-600"}`}>
                                  {calculatedQty > 0 ? calculatedQty.toLocaleString() : "—"}
                                </p>
                                
                              </div>
                            </div>

                            {qtyError && calculatedQty > 0 && (
                              <p className="text-[11px] text-red-400 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> {qtyError}
                              </p>
                            )}
                          </div>
                        )}

                        {/* profit percentage */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] text-slate-400 font-medium">نسبة الربح (%)</label>
                          <input
														placeholder="0.50"
                            type="number"
                            step="0.1"
                            min="0"
                            value={profitPercentage}
                            onChange={(e) => setProfitPercentage(Number(e.target.value))}
                            className="w-full rounded-xl border border-slate-700 px-3 py-2.5 bg-slate-800 text-white text-sm focus:outline-none focus:border-yellow-400/60 transition"
                          />
                        </div>

                        {/* account id */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] text-slate-400 font-medium">Account ID</label>
                          <input
                            value={accountId}
                            onChange={(e) => setAccountId(e.target.value)}
                            placeholder="أدخل account_id"
                            className="w-full rounded-xl border border-slate-700 px-3 py-2.5 bg-slate-800 text-white text-sm focus:outline-none focus:border-yellow-400/60 transition placeholder-slate-600"
                          />
                        </div>
                      </div>

                      {/* preview button */}
                      <button
                        onClick={handlePreview}
                        disabled={loadingPreview || !!qtyError || !effectiveQty}
                        className="w-full rounded-xl bg-yellow-400 text-black font-semibold py-2.5 text-sm hover:bg-yellow-300 active:bg-yellow-500 transition flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loadingPreview ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> جاري المعاينة...</>
                        ) : (
                          <><Eye className="w-4 h-4" /> عرض المعاينة</>
                        )}
                      </button>

                      {/* preview error */}
                      {previewError && (
                        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 space-y-1 animate-in fade-in duration-200">
                          <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            خطأ في المعاينة
                          </div>
                          <p className="text-red-300 text-xs leading-relaxed">{previewError}</p>
                        </div>
                      )}

                      {preview && <PreviewCard preview={preview} />}

                      {/* create order button */}
                      {(preview || previewError) && (
                        <button
                          onClick={handleCreateOrder}
                          disabled={!canCreate}
                          className={`w-full rounded-xl py-3 font-bold text-sm flex items-center justify-center gap-2 transition ${
                            canCreate
                              ? "bg-emerald-500 text-black hover:bg-emerald-400 active:bg-emerald-600 shadow-lg shadow-emerald-500/20"
                              : "bg-slate-700 text-slate-400 cursor-not-allowed"
                          }`}
                        >
                          {submitting ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> جاري إنشاء الطلب...</>
                          ) : (
                            <><Sparkles className="w-4 h-4" /> إنشاء الطلب</>
                          )}
                        </button>
                      )}
                    </>
                  )}

                  {/* loading details spinner */}
                  {loadingDetails && (
                    <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
                      <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
                      <span className="text-sm">جاري تحميل تفاصيل المنتج...</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderPopup;